import { Link } from '@/router';
import { GonjongSilhouette, SongketDivider } from '@/components/Motifs';

const STEPS = [
  { n: '1', t: 'Bersihkan Tangan di Kobokan', d: 'Makan dengan tangan adalah cara paling nikmat menikmati nasi Padang. Bilas jari di mangkuk kobokan berair jeruk sebelum dan sesudah makan.' },
  { n: '2', t: 'Pilih Lauk dari Meja Hidang', d: 'Dari deretan piring kecil di hadapan Anda, ambil lauk yang menggoda — sisanya biarkan terjaga rapi.' },
  { n: '3', t: 'Siram Kuah Gulai di Atas Nasi', d: 'Sendokkan kuah gulai kuning hangat ke atas nasi. Bagi banyak orang, inilah momen terbaiknya.' },
  { n: '4', t: 'Tutup dengan Sambal Ijo', d: 'Tambahkan sambal ijo atau balado sesuai keberanian Anda, lalu nikmati sampai suapan terakhir.' },
];

export default function Tentang() {
  return (
    <div data-testid="tentang-page">
      <section className="relative overflow-hidden border-b border-stone-200 bg-cream-2">
        <GonjongSilhouette className="pointer-events-none absolute -bottom-1 right-0 h-24 w-[420px] text-crimson opacity-[0.06]" />
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-20">
          <nav className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500" aria-label="Breadcrumb" data-testid="tentang-breadcrumb">
            <Link to="/" className="text-crimson hover:underline">Beranda</Link>
            <span className="mx-2" aria-hidden="true">›</span>
            <span aria-current="page">Tentang Kami</span>
          </nav>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
            Tentang Nasi Padang <em className="text-crimson">Sederhana</em>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-stone-600 sm:text-lg">
            Kami adalah rumah makan masakan Padang. Bagi kami, “sederhana” bukan berarti biasa —
            ia adalah janji keramahan: masakan rumahan khas Minangkabau yang jujur, bumbu yang
            dimasak tanpa jalan pintas, dan meja yang selalu terbuka untuk siapa saja.
          </p>
        </div>
        <SongketDivider />
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 md:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-turmeric">Budaya di Balik Meja</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl">
              Tradisi Hidang Khas Minangkabau
            </h2>
            <p className="mt-6 text-base leading-relaxed text-stone-600">
              Rumah makan Padang dikenal lewat cara penyajiannya yang unik di Nusantara: pelayan
              membawa belasan piring kecil sekaligus — direkatkan di lengan dengan keahlian khas —
              lalu menatanya memenuhi meja Anda. Rendang, gulai, ayam, sayur, dan sambal hadir
              bersamaan seperti jamuan kecil.
            </p>
            <p className="mt-4 text-base leading-relaxed text-stone-600">
              Anda bebas memilih. Hanya piring yang disantap yang dihitung saat membayar. Tradisi
              ini lahir dari budaya keramahan Minang: tamu harus melihat semua yang tersedia dan
              merasa dipersilakan sepenuhnya.
            </p>
            <p className="mt-4 text-base leading-relaxed text-stone-600">
              Di baliknya ada dapur yang bekerja sejak pagi: santan diperas, bumbu diulek, dan
              rendang diaduk perlahan hingga pekat. Itulah keseharian yang kami jaga.
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:col-span-6">
            <figure className="overflow-hidden rounded-3xl shadow-md sm:translate-y-6">
              <img
                src="/images/spread.jpg"
                alt="Sepiring nasi Padang lengkap dengan rendang, telur, dan kerupuk"
                loading="lazy"
                decoding="async"
                className="aspect-[4/5] w-full object-cover"
              />
            </figure>
            <figure className="overflow-hidden rounded-3xl shadow-md">
              <img
                src="/images/rice.jpg"
                alt="Nasi Padang di atas daun pisang dengan aneka lauk dan telur balado"
                loading="lazy"
                decoding="async"
                className="aspect-[4/5] w-full object-cover"
              />
            </figure>
          </div>
        </div>
      </section>

      <section className="bg-charcoal text-stone-200 texture-grain" data-testid="cara-menikmati-section">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 md:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-turmeric">Panduan Singkat</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-santan sm:text-4xl">
            Cara Menikmati Nasi Padang
          </h2>
          <ol className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s) => (
              <li key={s.n} className="rounded-3xl border border-stone-700/80 bg-stone-900/60 p-6" data-testid={`panduan-step-${s.n}`}>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-turmeric font-display text-lg font-bold text-charcoal">
                  {s.n}
                </span>
                <h3 className="mt-5 text-base font-bold text-santan">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-stone-400">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 md:py-24">
        <h2 className="font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl">Suasana Meja Kami</h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          <figure className="overflow-hidden rounded-3xl shadow-md">
            <img src="/images/feast.jpg" alt="Aneka hidangan Padang tersaji bersama di satu meja" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover transition-transform duration-500 hover:scale-105" />
          </figure>
          <figure className="overflow-hidden rounded-3xl shadow-md">
            <img src="/images/dishes.jpg" alt="Rangkaian masakan Indonesia dalam piring-piring kecil" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover transition-transform duration-500 hover:scale-105" />
          </figure>
          <figure className="overflow-hidden rounded-3xl shadow-md">
            <img src="/images/sate.jpg" alt="Sate dibakar di atas arang hingga harum" loading="lazy" decoding="async" className="aspect-[4/3] w-full object-cover transition-transform duration-500 hover:scale-105" />
          </figure>
        </div>
        <div className="mt-14 flex flex-col items-start gap-6 rounded-3xl bg-crimson p-8 text-santan md:flex-row md:items-center md:justify-between md:p-10" data-testid="tentang-cta-section">
          <div>
            <h2 className="font-display text-2xl font-bold sm:text-3xl">Siap Menyusun Piring Anda?</h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-santan/85">
              Lihat daftar hidangan lengkap dan temukan lauk favorit Anda sebelum mampir.
            </p>
          </div>
          <Link
            to="/menu/"
            data-testid="tentang-cta-menu"
            className="inline-flex shrink-0 items-center rounded-full bg-santan px-7 py-3.5 text-sm font-bold text-crimson transition-colors duration-200 hover:bg-turmeric-soft"
          >
            Buka Daftar Hidangan
          </Link>
        </div>
      </section>
    </div>
  );
}
